import gsap from "gsap";

const LOG_PREFIX = "[shadow]";

export const shadow = (): void => {
  const calculateOpacityScrollValue = (): number => {
    const value = (window.innerHeight * 0.7 - window.scrollY) / (window.innerHeight * 0.7);
    return Math.max(0, Math.min(value, 1));
  };

  let opacityScrollValue = calculateOpacityScrollValue();
  let lastPointerPosition = {
    clientX: window.innerWidth / 2 + 400,
    clientY: window.innerHeight / 2 - 400,
  };

  const getElementCenter = (element: HTMLElement): { x: number; y: number } => {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  };

  const updateDropShadow = (e: MouseEvent | { clientX: number; clientY: number }): void => {
    const pointerX = e.clientX ?? lastPointerPosition.clientX;
    const pointerY = e.clientY ?? lastPointerPosition.clientY;

    // Update .filter-shadow elements
    const filterShadowElements = document.querySelectorAll<HTMLElement>(".filter-shadow");
    console.debug(LOG_PREFIX, "filter-shadow elements found:", filterShadowElements.length);

    if (filterShadowElements.length === 0) {
      console.warn(LOG_PREFIX, "No .filter-shadow elements found");
    }

    filterShadowElements.forEach((element) => {
      const center = getElementCenter(element);
      const dx = pointerX - center.x;
      const dy = pointerY - center.y;
      const angle = Math.atan2(dy, dx);
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      const intensity = Math.min(distance / 800, 0.35);

      const brightX = Math.cos(angle) * intensity;
      const brightY = Math.sin(angle) * intensity;
      const darkX = -brightX;
      const darkY = -brightY;

      const filterValue = `drop-shadow(${brightX}rem ${brightY}rem 0.125rem rgba(255, 255, 255, ${0.75 * opacityScrollValue})) drop-shadow(${darkX}rem ${darkY}rem 0.125rem rgba(168, 168, 168, ${0.24 * opacityScrollValue}))`;

      console.debug(LOG_PREFIX, "Applying filter to element:", {
        element,
        center,
        pointerX,
        pointerY,
        angle,
        intensity,
        filterValue,
        opacityScrollValue,
      });

      gsap.to(element, {
        filter: filterValue,
        duration: 1.5,
        ease: "power2.out",
      });
    });

    // Update .box-shadow elements
    const boxShadowElements = document.querySelectorAll<HTMLElement>(".box-shadow");
    console.debug(LOG_PREFIX, "box-shadow elements found:", boxShadowElements.length);

    if (boxShadowElements.length === 0) {
      console.warn(LOG_PREFIX, "No .box-shadow elements found");
    }

    boxShadowElements.forEach((element) => {
      const center = getElementCenter(element);
      const dx = pointerX - center.x;
      const dy = pointerY - center.y;
      const angle = Math.atan2(dy, dx);
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      const intensity = Math.min(distance / 800, 0.35);

      const brightX = Math.cos(angle) * intensity;
      const brightY = Math.sin(angle) * intensity;
      const darkX = -brightX;
      const darkY = -brightY;

      // .box-shadow elements always use full opacity (not affected by scroll)
      const boxShadowOpacity = 1;
      const boxShadowValue = `${brightX * 1.5}rem ${brightY * 1.5}rem ${0.125 * 1.5}rem rgba(255, 255, 255, ${0.75 * boxShadowOpacity}), ${darkX * 1.5}rem ${darkY * 1.5}rem ${0.125 * 2.5}rem rgba(168, 168, 168, ${0.24 * boxShadowOpacity}), inset ${darkX * 0.5}rem ${darkY * 0.5}rem ${0.125 * 1.5}rem rgba(255, 255, 255, ${0.16 * boxShadowOpacity})`;

      console.debug(LOG_PREFIX, "Applying boxShadow to element:", {
        element,
        center,
        pointerX,
        pointerY,
        angle,
        intensity,
        boxShadowValue,
        boxShadowOpacity,
      });

      gsap.to(element, {
        boxShadow: boxShadowValue,
        duration: 1.5,
        ease: "power2.out",
      });
    });
  };

  // Wait for DOM to be ready and ensure elements exist
  let retryCount = 0;
  const MAX_RETRIES = 50; // 5 seconds max (50 * 100ms)

  const initializeShadow = () => {
    const filterShadowElements = document.querySelectorAll<HTMLElement>(".filter-shadow");
    const boxShadowElements = document.querySelectorAll<HTMLElement>(".box-shadow");

    if (filterShadowElements.length === 0 && boxShadowElements.length === 0) {
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.warn(LOG_PREFIX, `No shadow elements found, retrying... (${retryCount}/${MAX_RETRIES})`);
        setTimeout(initializeShadow, 100);
        return;
      } else {
        console.error(LOG_PREFIX, "No shadow elements found after max retries. Make sure .filter-shadow or .box-shadow elements exist in the DOM.");
        return;
      }
    }

    console.log(LOG_PREFIX, "Initializing shadow with elements:", {
      filterShadow: filterShadowElements.length,
      boxShadow: boxShadowElements.length,
    });

    updateDropShadow(lastPointerPosition);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeShadow);
  } else {
    // Small delay to ensure all elements are rendered
    setTimeout(initializeShadow, 100);
  }

  window.addEventListener("mousemove", (e) => {
    lastPointerPosition = {
      clientX: e.clientX,
      clientY: e.clientY,
    };
    updateDropShadow(e);
  });

  window.addEventListener("scroll", () => {
    opacityScrollValue = 1;
    console.debug(LOG_PREFIX, "Scroll opacity value:", opacityScrollValue);
    updateDropShadow(lastPointerPosition);
  });
};
